import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUp,
  Check,
  Clock,
  Loader2,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { dominantDirection, formatTimecode } from "@rec/shared";
import { API_BASE, api } from "@/lib/api";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useServerText } from "@/i18n";
import { useFormat, type Formatter } from "@/i18n/format";

/**
 * Chat about the meeting, with its history.
 *
 * Chats are stored per person: everyone in the workspace reads the same summary
 * and the same chapters, but nobody reads anybody else's questions. That is
 * enforced on the server — this panel only ever sees its own.
 *
 * Answers stream as SSE. Citations arrive as a separate event after the text —
 * the model can split a uuid across two deltas, and a half-parsed id rendered
 * as a chip is worse than one that appears a beat late.
 *
 * Every chip is a moment the backend resolved from a real utterance, so
 * clicking one always lands somewhere. See CLAUDE.md invariant 3.
 */

type Citation = {
  utteranceId: string;
  startMs: number;
  speakerLabel: string;
  snippet: string;
};

type Message = {
  id: string;
  role: "user" | "assistant";
  content: string;
  citations: Citation[];
  streaming?: boolean;
};

/** Relative until it stops being useful, then a date. Same rule as any inbox. */
function whenLabel(iso: string, t: TFunction<["ai", "common"]>, f: Formatter): string {
  const then = new Date(iso).getTime();
  const minutes = Math.round((Date.now() - then) / 60_000);

  if (minutes < 1) return t("chat.when.justNow");
  if (minutes < 60) return t("chat.when.minutesAgo", { count: minutes });
  if (minutes < 60 * 24) return t("chat.when.hoursAgo", { count: Math.round(minutes / 60) });
  if (minutes < 60 * 24 * 7)
    return t("chat.when.daysAgo", { count: Math.round(minutes / (60 * 24)) });
  return f.date(iso, { dateStyle: "medium" });
}

export function ChatPanel({
  recordingId,
  onSeek,
  onCitationsChange,
}: {
  recordingId: string;
  onSeek: (ms: number) => void;
  /** Lets the transcript mark the cited lines. */
  onCitationsChange?: (utteranceIds: Set<string>) => void;
}) {
  const { t } = useTranslation(["ai", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const [messages, setMessages] = useState<Message[]>([]);
  const [chatId, setChatId] = useState<string | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  /** Mirrors `chatId` for reads inside the streaming loop, where state is stale. */
  const chatIdRef = useRef<string | null>(null);

  const chats = useQuery({
    queryKey: ["chats", recordingId],
    queryFn: () => api.listChats(recordingId),
  });

  // Stick to the bottom while an answer streams in.
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  useEffect(() => () => abortRef.current?.abort(), []);

  /**
   * Open a stored chat.
   *
   * Messages are fetched rather than cached in this component, because the
   * whole reason history exists is that it outlives the component — closing the
   * panel, switching recordings, or reloading the page must not lose anything.
   */
  const openChat = useCallback(
    async (id: string) => {
      abortRef.current?.abort();
      setShowHistory(false);
      setError(null);

      try {
        const { chat, messages: stored } = await api.getChat(id);
        chatIdRef.current = chat.id;
        setChatId(chat.id);
        setTitle(chat.title);
        setMessages(
          stored.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            citations: m.citations,
          })),
        );
        onCitationsChange?.(
          new Set(stored.flatMap((m) => m.citations.map((ci) => ci.utteranceId))),
        );
      } catch {
        setError(t("chat.couldNotOpen"));
      }
    },
    [onCitationsChange, t],
  );

  /** Start fresh. Nothing is saved until the first question is answered. */
  const newChat = useCallback(() => {
    abortRef.current?.abort();
    chatIdRef.current = null;
    setChatId(null);
    setTitle(null);
    setMessages([]);
    setError(null);
    setShowHistory(false);
    onCitationsChange?.(new Set());
  }, [onCitationsChange]);

  const removeChat = useMutation({
    mutationFn: (id: string) => api.deleteChat(id),
    onSuccess: (_result, id) => {
      void queryClient.invalidateQueries({ queryKey: ["chats", recordingId] });
      // Only clear the view if the open conversation is the one deleted.
      if (id === chatId) newChat();
    },
  });

  const removeMessage = useMutation({
    mutationFn: ({ id }: { id: string }) => api.deleteChatMessage(chatId!, id),
    onSuccess: (result) => {
      // The server decides what went: deleting a question takes its answer
      // with it, so the client must not assume it was only the one row.
      const gone = new Set(result.deleted);
      setMessages((prev) => prev.filter((m) => !gone.has(m.id)));
    },
  });

  async function ask() {
    const question = input.trim();
    if (!question || streaming) return;

    setInput("");
    setError(null);
    setStreaming(true);

    const assistantId = `pending-${Date.now()}`;
    setMessages((prev) => [
      ...prev,
      { id: `user-${Date.now()}`, role: "user", content: question, citations: [] },
      { id: assistantId, role: "assistant", content: "", citations: [], streaming: true },
    ]);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // Same base as every other call — a relative path here reaches the SPA
      // shell in production, not the API.
      const response = await fetch(`${API_BASE}/recordings/${recordingId}/ask`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question, chatId: chatId ?? undefined }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error?.message ?? t("chat.unavailable"));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        // SSE frames end with a blank line, and a frame can span two reads.
        const frames = buffer.split("\n\n");
        buffer = frames.pop() ?? "";

        for (const frame of frames) {
          const eventLine = frame.split("\n").find((l) => l.startsWith("event: "));
          const dataLine = frame.split("\n").find((l) => l.startsWith("data: "));
          if (!eventLine || !dataLine) continue;

          const event = eventLine.slice(7).trim();
          const payload = JSON.parse(dataLine.slice(6));

          if (event === "start") {
            chatIdRef.current = payload.chatId;
            setChatId(payload.chatId);
          } else if (event === "token") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, content: m.content + payload.text } : m,
              ),
            );
          } else if (event === "citations") {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, citations: payload.citations } : m,
              ),
            );
            onCitationsChange?.(
              new Set(payload.citations.map((ci: Citation) => ci.utteranceId)),
            );
          } else if (event === "title") {
            // Named from its first exchange, so the history list reads well
            // without anyone having to title anything.
            setTitle(payload.title);
          } else if (event === "error") {
            throw new Error(payload.message);
          }
        }
      }

      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)),
      );

      void queryClient.invalidateQueries({ queryKey: ["chats", recordingId] });

      /**
       * Re-read the thread so the optimistic rows pick up their real ids.
       *
       * Without this a message just sent cannot be deleted until the page is
       * reloaded — the client is holding `pending-…` placeholders that mean
       * nothing to the server. Reading `chatId` from a ref rather than state
       * because the `start` event may have set it during this same render pass.
       */
      const settledId = chatIdRef.current;
      if (settledId) {
        const { messages: stored } = await api.getChat(settledId);
        setMessages(
          stored.map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            citations: m.citations,
          })),
        );
      }
    } catch (caught) {
      if (controller.signal.aborted) return;
      setError(caught instanceof Error ? caught.message : t("chat.somethingWrong"));
      // Drop the empty assistant bubble rather than leaving a blank turn.
      setMessages((prev) =>
        prev.filter((m) => !(m.id === assistantId && m.content === "")),
      );
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  return (
    <div className="relative flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-1 border-b border-border px-2 py-1.5">
        <button
          type="button"
          onClick={() => setShowHistory((v) => !v)}
          className="inline-flex min-w-0 flex-1 items-center gap-1.5 rounded-md px-2 py-1 text-left text-[12.5px] text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
        >
          <Clock size={13} strokeWidth={2} className="shrink-0" />
          <span className="truncate">
            {title ?? (chatId ? t("chat.untitled") : t("chat.newConversation"))}
          </span>
          {chats.data && chats.data.length > 0 ? (
            <span className="shrink-0 tabular-nums text-text-faint">
              {chats.data.length}
            </span>
          ) : null}
        </button>

        <button
          type="button"
          onClick={newChat}
          title={t("chat.newConversation")}
          aria-label={t("chat.newConversation")}
          className="shrink-0 rounded-md p-1.5 text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
        >
          <Plus size={15} />
        </button>
      </div>

      {showHistory ? (
        <ChatHistory
          chats={chats.data ?? []}
          loading={chats.isLoading}
          activeId={chatId}
          onOpen={(id) => void openChat(id)}
          onDelete={(id) => removeChat.mutate(id)}
          onRename={async (id, next) => {
            await api.renameChat(id, next);
            if (id === chatId) setTitle(next);
            void queryClient.invalidateQueries({ queryKey: ["chats", recordingId] });
          }}
          onClose={() => setShowHistory(false)}
        />
      ) : null}

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto p-4">
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center px-6 text-center">
            <Sparkles size={26} strokeWidth={1.5} className="mb-3 text-text-faint" />
            <p className="text-sm font-medium text-text">{t("chat.emptyTitle")}</p>
            <p className="mt-1.5 max-w-[260px] text-[13px] leading-relaxed text-text-muted">
              {t("chat.emptyBody")}
            </p>
          </div>
        ) : (
          messages.map((message) => {
            // A message only becomes deletable once the server has given it a
            // real id; the optimistic rows are replaced on the next load.
            const deletable =
              !message.streaming &&
              chatId !== null &&
              // Placeholders exist only between sending and the refetch that
              // replaces them with real rows; deleting one would 404.
              !message.id.startsWith("pending-") &&
              !message.id.startsWith("user-");

            return (
              <div key={message.id} className="group/msg relative">
                {message.role === "user" ? (
                  <div className="flex items-start justify-end gap-1">
                    {deletable ? (
                      <DeleteMessage
                        onDelete={() => removeMessage.mutate({ id: message.id })}
                        label={t("chat.deleteQuestion")}
                      />
                    ) : null}
                    {/*
                      `dir="auto"` because the content is whatever language the
                      meeting was in. Without it the browser lays the text out
                      with an LTR base direction, and the bidi algorithm then
                      reorders an Arabic sentence around any Latin word inside
                      it — the words that should precede "vendor" end up after
                      it. Setting the base direction from the text's own first
                      strong character is what puts the run back in order.
                    */}
                    <p
                      dir={dominantDirection(message.content)}
                      className="max-w-[85%] rounded-2xl rounded-br-md bg-accent px-3.5 py-2 text-[14px] leading-relaxed text-accent-fg"
                    >
                      {message.content}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    <div className="flex items-start gap-1">
                      {/*
                        Per line, not per message.

                        `dir="auto"` takes the base direction from the first
                        strong character of whatever it is on, so one attribute
                        on the whole answer would let an English opening line
                        force every Arabic line beneath it into LTR. An answer
                        that mixes languages line by line — which a bilingual
                        meeting produces constantly — needs each line to decide
                        for itself.
                      */}
                      <div className="min-w-0 flex-1 text-[14px] leading-relaxed text-text">
                        {message.content
                          // Strip the raw [uuid] markers — they become chips below.
                          .replace(
                            /\s*\[[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\]/gi,
                            "",
                          )
                          .split("\n")
                          .map((line, index) => (
                            <p
                              key={index}
                              dir={dominantDirection(line)}
                              // Preserves the blank lines that `whitespace-pre-wrap`
                              // used to render, without collapsing an empty line.
                              className="min-h-[1lh] whitespace-pre-wrap"
                            >
                              {line}
                            </p>
                          ))}
                        {message.streaming ? (
                          <span className="ml-0.5 inline-block h-[15px] w-[2px] animate-pulse bg-accent align-middle" />
                        ) : null}
                      </div>
                      {deletable ? (
                        <DeleteMessage
                          onDelete={() => removeMessage.mutate({ id: message.id })}
                          label={t("chat.deleteAnswer")}
                        />
                      ) : null}
                    </div>

                    {message.citations.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {message.citations.map((citation) => (
                          <button
                            key={citation.utteranceId}
                            type="button"
                            onClick={() => onSeek(citation.startMs)}
                            title={citation.snippet}
                            className={cn(
                              "inline-flex items-center gap-1.5 rounded-full",
                              "border border-border bg-bg-subtle px-2.5 py-1",
                              "text-[12px] transition-colors hover:border-accent hover:bg-accent-subtle",
                            )}
                          >
                            <span className="font-medium text-text">
                              {citation.speakerLabel}
                            </span>
                            <span className="font-mono tabular-nums text-text-muted">
                              {formatTimecode(citation.startMs)}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })
        )}

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger">
            {st(error)}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            dir="auto"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              // Enter sends, Shift+Enter breaks the line — what people expect
              // from a chat box, not a form.
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void ask();
              }
            }}
            rows={1}
            placeholder={t("chat.placeholder")}
            disabled={streaming}
            className={cn(
              "max-h-32 min-h-[38px] flex-1 resize-none rounded-lg border border-border",
              "bg-bg px-3 py-2 text-sm text-text placeholder:text-text-faint",
              "transition-colors focus:border-accent focus:outline-none disabled:opacity-60",
            )}
          />
          <Button
            variant="primary"
            size="icon"
            onClick={() => void ask()}
            disabled={streaming || input.trim().length === 0}
            aria-label={t("common:actions.send")}
          >
            {streaming ? (
              <Loader2 size={15} className="animate-spin" />
            ) : (
              <ArrowUp size={15} />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Delete control for a single message.
 *
 * Two-step, because there is no undo: the first click arms it, the second
 * commits. Reserved space rather than hover-only appearance, so the layout
 * never jumps under the cursor.
 */
function DeleteMessage({
  onDelete,
  label,
}: {
  onDelete: () => void;
  label: string;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) return;
    // Disarm on its own, so a stray click does not leave a live delete button
    // sitting in the conversation indefinitely.
    const timer = setTimeout(() => setArmed(false), 3_000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={() => {
        if (armed) onDelete();
        else setArmed(true);
      }}
      className={cn(
        "mt-1 shrink-0 rounded-md p-1 transition-all",
        armed
          ? "bg-danger-subtle text-danger opacity-100"
          : "text-text-faint opacity-0 group-hover/msg:opacity-100 focus-visible:opacity-100",
        "hover:text-danger",
      )}
    >
      {armed ? <Check size={13} /> : <Trash2 size={13} />}
    </button>
  );
}

/**
 * The list of past conversations.
 *
 * Drawn over the thread rather than beside it: the panel is 400px wide and a
 * permanent sidebar inside it would leave no room for the answers.
 */
function ChatHistory({
  chats,
  loading,
  activeId,
  onOpen,
  onDelete,
  onRename,
  onClose,
}: {
  chats: Array<{ id: string; title: string | null; updatedAt: string }>;
  loading: boolean;
  activeId: string | null;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation(["ai", "common"]);
  const f = useFormat();
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [confirming, setConfirming] = useState<string | null>(null);

  return (
    <div className="absolute inset-x-0 top-[37px] bottom-0 z-10 flex flex-col bg-surface">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-[12.5px] font-medium text-text">{t("chat.history.title")}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("chat.history.close")}
          className="rounded-md p-1 text-text-faint transition-colors hover:bg-surface-hover hover:text-text"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-1.5">
        {loading ? (
          <p className="p-4 text-center text-[13px] text-text-muted">{t("common:states.loading")}</p>
        ) : chats.length === 0 ? (
          <p className="p-4 text-center text-[13px] leading-relaxed text-text-muted">
            {t("chat.history.empty")}
          </p>
        ) : (
          chats.map((chat) => (
            <div
              key={chat.id}
              className={cn(
                "group/chat flex items-center gap-1 rounded-lg px-2 py-1.5",
                chat.id === activeId ? "bg-accent-subtle" : "hover:bg-surface-hover",
              )}
            >
              {renaming === chat.id ? (
                <form
                  className="flex flex-1 items-center gap-1"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const next = draft.trim();
                    if (next) await onRename(chat.id, next);
                    setRenaming(null);
                  }}
                >
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={() => setRenaming(null)}
                    autoFocus
                    className="h-7 min-w-0 flex-1 rounded-md border border-accent bg-bg px-2 text-[13px] text-text focus:outline-none"
                  />
                  <button
                    type="submit"
                    aria-label={t("chat.history.saveName")}
                    // Commit before the blur that would cancel the rename.
                    onMouseDown={(e) => e.preventDefault()}
                    className="rounded-md p-1 text-text-muted hover:text-text"
                  >
                    <Check size={13} />
                  </button>
                </form>
              ) : confirming === chat.id ? (
                <div className="flex flex-1 items-center justify-between gap-2">
                  <span className="text-[12.5px] text-danger">{t("chat.history.confirmDelete")}</span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        onDelete(chat.id);
                        setConfirming(null);
                      }}
                      className="rounded-md bg-danger-subtle px-2 py-0.5 text-[12px] font-medium text-danger"
                    >
                      {t("common:actions.delete")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirming(null)}
                      className="rounded-md px-2 py-0.5 text-[12px] text-text-muted hover:text-text"
                    >
                      {t("common:actions.keep")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => onOpen(chat.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block truncate text-[13px] text-text">
                      {chat.title ?? t("chat.untitled")}
                    </span>
                    <span className="block text-[11.5px] text-text-faint">
                      {whenLabel(chat.updatedAt, t, f)}
                    </span>
                  </button>

                  <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover/chat:opacity-100 focus-within:opacity-100">
                    <button
                      type="button"
                      aria-label={t("common:actions.rename")}
                      title={t("common:actions.rename")}
                      onClick={() => {
                        setDraft(chat.title ?? "");
                        setRenaming(chat.id);
                      }}
                      className="rounded-md p-1 text-text-faint hover:text-text"
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      type="button"
                      aria-label={t("chat.history.deleteConversation")}
                      title={t("chat.history.deleteConversation")}
                      onClick={() => setConfirming(chat.id)}
                      className="rounded-md p-1 text-text-faint hover:text-danger"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
